import { useState } from 'react';
import { Icon } from '@/components/Icon';
import { useTooltip } from '@/hooks/useTooltip';
import { usePermissions } from '@/hooks/usePermissions';
import { useConfirmDialog } from '@/hooks/useConfirmDialog';
import { showToast } from '@/lib/toast';
import { deleteAd, type AdRow } from '@/api/ads';
import { deleteBanner } from '@/api/banners';
import { AdsTab } from './AdsTab';
import { BannersTab, type BannerFormMode } from './BannersTab';

type Tab = 'banners' | 'ads';

/** У вкладок разные права: баннеры — platform.marketing, объявления — platform.ads. */
const TABS: { key: Tab; label: string; permission: string }[] = [
  { key: 'banners', label: 'Баннеры', permission: 'platform.marketing' },
  { key: 'ads', label: 'Объявления', permission: 'platform.ads' },
];

/** Режим вкладки объявлений: список, создание или правка конкретного объявления. */
export type AdFormMode = { mode: 'create' } | { mode: 'edit'; ad: AdRow } | null;

/**
 * «Маркетинг» — две вкладки: баннеры и объявления (Orchid Marketing*Screen и
 * Ad*Screen соответственно). Разные модели, права и наборы полей — две
 * вкладки, не общий компонент (тот же приём, что в EventsPage).
 *
 * Режимы форм живут здесь, а не во вкладках: кнопки «Добавить»/«Удалить»
 * стоят в шапке страницы. Пока открыта любая форма, табы скрыты целиком —
 * создание/правка выглядят как отдельная страница, а не как содержимое
 * вкладки (в Orchid это и были отдельные экраны со своими маршрутами).
 */
export function MarketingPage() {
  const { can } = usePermissions();
  // Показываем только доступные вкладки; открытой делаем первую из них —
  // иначе админ с правом лишь на объявления упирался бы в чужой раздел.
  const tabs = TABS.filter((t) => can(t.permission));
  const [tab, setTab] = useState<Tab>(tabs[0]?.key ?? 'banners');
  const [adForm, setAdForm] = useState<AdFormMode>(null);
  const [bannerForm, setBannerForm] = useState<BannerFormMode>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [deleting, setDeleting] = useState(false);
  const { confirm, dialog } = useConfirmDialog();
  const deleteTooltip = useTooltip<HTMLButtonElement>('Удалить');

  const reload = () => setReloadToken((t) => t + 1);
  const editingAd = adForm?.mode === 'edit' ? adForm.ad : null;
  const editingBanner = bannerForm?.mode === 'edit' ? bannerForm.banner : null;
  const formOpen = adForm !== null || bannerForm !== null;

  const closeForms = () => {
    setAdForm(null);
    setBannerForm(null);
  };

  const deleteEditing = async () => {
    const target = editingAd
      ? { kind: 'объявление' as const, name: editingAd.name, remove: () => deleteAd(editingAd.id) }
      : editingBanner
        ? { kind: 'баннер' as const, name: editingBanner.name, remove: () => deleteBanner(editingBanner.id) }
        : null;
    if (!target) return;

    const ok = await confirm(
      `Удалить ${target.kind} «${target.name}»? После удаления все данные будут удалены безвозвратно.`,
      { confirmLabel: 'Удалить', danger: true },
    );
    if (!ok) return;

    setDeleting(true);
    try {
      await target.remove();
      closeForms();
      showToast(target.kind === 'объявление' ? 'Объявление удалено' : 'Баннер удалён');
      reload();
    } catch {
      showToast('Не удалось удалить.');
    } finally {
      setDeleting(false);
    }
  };

  const title = formTitle(adForm, bannerForm);

  return (
    <section className="catalog">
      <header className="catalog__header catalog__header--row">
        <div>
          <h1>{title}</h1>
          <p className="catalog__lede">
            {formOpen
              ? 'Заполните данные для показа в кабинетах клиентов.'
              : 'Баннеры и объявления для личных кабинетов клиентов.'}
          </p>
        </div>

        {!formOpen && tab === 'ads' && (
          <button type="button" className="btn btn--primary" onClick={() => setAdForm({ mode: 'create' })}>
            <Icon name="plus" size={15} /> Добавить объявление
          </button>
        )}
        {!formOpen && tab === 'banners' && (
          <button type="button" className="btn btn--primary" onClick={() => setBannerForm({ mode: 'create' })}>
            <Icon name="plus" size={15} /> Добавить баннер
          </button>
        )}
        {(editingAd || editingBanner) && (
          <button
            ref={deleteTooltip.ref}
            type="button"
            className="btn btn--icon-danger"
            onClick={deleteEditing}
            disabled={deleting}
            aria-label={editingAd ? 'Удалить объявление' : 'Удалить баннер'}
            {...deleteTooltip.handlers}
          >
            <Icon name="trash" size={17} />
            {deleteTooltip.node}
          </button>
        )}
      </header>

      <div className="card tabs">
        {/* Пока открыта форма — табы не показываем вовсе: это отдельный экран. */}
        {!formOpen && (
          <div className="tabs__list" role="tablist">
            {tabs.map((t) => (
              <button
                key={t.key}
                type="button"
                role="tab"
                aria-selected={tab === t.key}
                className={`tabs__item${tab === t.key ? ' tabs__item--active' : ''}`}
                onClick={() => setTab(t.key)}
              >
                {t.label}
              </button>
            ))}
          </div>
        )}

        <div className="tabs__panel" role="tabpanel">
          {tab === 'ads' ? (
            <AdsTab
              key="ads"
              form={adForm}
              onOpenForm={setAdForm}
              onCloseForm={closeForms}
              reloadToken={reloadToken}
              onReload={reload}
            />
          ) : (
            <BannersTab
              key="banners"
              form={bannerForm}
              onOpenForm={setBannerForm}
              onCloseForm={closeForms}
              reloadToken={reloadToken}
              onReload={reload}
            />
          )}
        </div>
      </div>

      {dialog}
    </section>
  );
}

function formTitle(adForm: AdFormMode, bannerForm: BannerFormMode): string {
  if (adForm?.mode === 'create') return 'Новое объявление';
  if (adForm?.mode === 'edit') return `Редактирование: ${adForm.ad.name}`;
  if (bannerForm?.mode === 'create') return 'Новый баннер';
  if (bannerForm?.mode === 'edit') return `Редактирование: ${bannerForm.banner.name}`;
  return 'Маркетинг';
}
